/**
 * Authentication Context
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { apiClient } from '../lib/api'

interface AuthUser {
  userId: string
  accountId: string
  email: string
  role: 'owner' | 'admin' | 'member'
  isSystemAdmin: boolean
  emailVerified: boolean
  pendingEmail: string | null
  hasPassword: boolean
  authProviders: string[]
  tosAcceptedAt: string | null
  tosVersion: string | null
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  checkSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkSession = useCallback(async () => {
    try {
      const response = await apiClient.get<AuthUser>('/auth/me')
      if (response.success && response.data) {
        setUser(response.data)
      } else {
        setUser(null)
      }
    } catch (error) {
      // Session invalid or expired
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkSession()
  }, [checkSession])

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    const response = await apiClient.post<AuthUser>('/auth/login', {
      email,
      password,
      rememberMe,
    })

    if (response.success && response.data) {
      setUser(response.data)
    } else {
      throw new Error(response.error || 'Login failed')
    }
  }

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      // ユーザー切り替え時に前ユーザーのデータが残らないよう localStorage をクリア
      // （背景ジョブは前ユーザーのプロジェクトIDを保持し、再ログイン後もポーリングし続けてしまう）
      try {
        localStorage.removeItem('referralCode')
        localStorage.removeItem('background-jobs-v1')
      } catch { /* localStorage が使えない環境では何もしない */ }
      // TanStack Query のキャッシュ(クレジット残高・設定・プロジェクト一覧)と Zustand ストアは
      // メモリ上に残るため、フルリロードで確実に破棄する（共有 PC で次のユーザーに前ユーザーの
      // データが最大 staleTime の間見えるのを防ぐ）
      window.location.replace('/login')
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    checkSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
